import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { HiOutlineExclamation } from 'react-icons/hi';
import { refreshBasket } from '../../../store/basket/basketSlice';
import {
  HiOutlineX,
  HiOutlineShoppingBag,
  HiOutlineArrowRight,
  HiOutlineTag,
  HiOutlinePencilAlt,
  HiOutlineArrowLeft,
} from 'react-icons/hi';
import BasketProduct from '../../items/BasketProduct';
import Dialog from '../../ui/Dialog';
import NoteModal from './components/NoteModal';
import CouponModal from './components/CouponModal';
import useCouponMinPriceInfo from '../../../utils/hooks/useCouponMinPriceInfo';
import useAuth from '../../../utils/hooks/useAuth';
import { getNumberFormat } from '../../../utils/getNumberFormat';
import useBasketMenuIndex from '../../../utils/hooks/useBasketMenuIndex';
import { basketInMenuOrder } from '../../../utils/menuOrder';

/**
 * Redesigned cart sidebar — premium pass.
 * - Store mini-identity in header (logo + name) so customers know
 *   which restaurant they're ordering from.
 * - Inline OrderModeToggle + ScheduleToggle instead of a modal trigger
 *   so adjustments feel native to the cart surface.
 * - Section labels, savings highlight, and a refined checkout CTA.
 */
const CartSidebar = ({ isOpen, onClose, onLoginRequired }) => {
  const basket = useSelector((state) => state.basket.basket);
  const currentStore = useSelector((state) => state.base.common.currentStore);
  const { authenticated } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const couponMinPriceInfo = useCouponMinPriceInfo();
  const menuIndex = useBasketMenuIndex();

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);

  const basketItemQty = basket.items.reduce((a, b) => a + b.qty, 0);
  const subtotal =
    basket?.prices?.subtotal ?? basket.items.reduce((a, b) => a + b.total, 0);
  const discount = basket?.prices?.discountprice || 0;
  const hasSavings = discount > 0;

  const handleCheckout = () => {
    if (!authenticated) {
      onLoginRequired?.();
      onClose();
      return;
    }
    // Always check out under the store whose items are in the basket,
    // even if the user happens to be browsing a different store page.
    const basketSlug = basket?.basketStore?.storeSlug;
    const slug = basketSlug || currentStore.slug;
    navigate(`/${slug}/payment`);
    onClose();
  };

  return (
    <>
      <style>{`
        .csnew-root {
          position: fixed;
          inset: 0;
          z-index: 110; /* above CondensedHeader (z-100) so it never bleeds over the open cart */
          pointer-events: ${isOpen ? 'auto' : 'none'};
          /* The closed panel is parked at translateX(100%), one full screen
             width off-canvas to the right. Without clipping it here, that
             off-canvas box extends the page's rendered width on iOS Safari,
             which then shrink-to-fits / horizontally rubber-bands and exposes
             white beside the page ("the page slides left-right"). Clip ONLY
             the X axis (overflow-x: clip keeps overflow-y visible, unlike
             hidden) so the panel's ::after bottom cover — which extends
             vertically below the panel — is preserved. */
          overflow-x: clip;
        }
        .csnew-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(10, 10, 10, 0.42);
          transition: opacity 0.3s;
          opacity: ${isOpen ? 1 : 0};
        }
        .csnew-panel {
          position: absolute;
          top: 0; right: 0;
          height: 100vh;
          height: 100dvh;
          width: 100%;
          max-width: 460px;
          background: var(--ui-surface);
          display: flex;
          flex-direction: column;
          transform: translateX(${isOpen ? '0' : '100%'});
          transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
          box-shadow: -20px 0 60px -20px rgba(15, 23, 42, 0.2);
        }
        /* iOS 26 Liquid Glass: extend the panel's white below the dynamic
           viewport so the page canvas never shows behind Safari's bottom
           chrome while the cart is open (same fix as the product sheet).
           Spans through the large viewport bottom so the URL-bar band is
           covered in every chrome state. */
        .csnew-panel::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          height: 120px;
          height: calc(100lvh - 100dvh + 120px);
          background: var(--ui-surface);
          pointer-events: none;
        }
      `}</style>

      <div className="csnew-root">
        <div className="csnew-backdrop" onClick={onClose} />
        <div className="csnew-panel">
          {/* ===================== HEADER ===================== */}
          <div
            className="flex-shrink-0 relative"
            style={{
              padding: 'max(var(--sat), 16px) 18px 14px',
              borderBottom: '1px solid var(--ui-line)',
              background: 'var(--ui-surface)',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close cart"
              className="absolute grid place-items-center transition-colors"
              style={{
                top: 'calc(var(--sat) + 12px)',
                right: '14px',
                width: '34px',
                height: '34px',
                borderRadius: '10px',
                background: 'var(--ui-surface-2)',
                color: 'var(--ui-ink-2)',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <HiOutlineX size={18} />
            </button>

            <h2
              style={{
                fontSize: '22px',
                fontWeight: 800,
                letterSpacing: '-0.03em',
                color: 'var(--ui-ink)',
                lineHeight: 1.1,
              }}
            >
              Your cart
              {basketItemQty > 0 && (
                <span
                  className="ml-2"
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'var(--ui-ink-3)',
                    letterSpacing: 0,
                  }}
                >
                  · {basketItemQty} item{basketItemQty !== 1 ? 's' : ''}
                </span>
              )}
            </h2>
          </div>

          {basket.items.length === 0 ? (
            <EmptyState onClose={onClose} />
          ) : (
            <>
              {/* ==================== ITEMS ==================== */}
              <div
                className="flex-1 overflow-y-auto min-h-0"
                style={{
                  padding: '12px 18px 4px',
                  scrollbarWidth: 'none',
                }}
              >
                <style>{`.cs-items::-webkit-scrollbar { display: none; }`}</style>
                <div className="cs-items">
                  <CrossStoreBanner
                    basketStore={basket?.basketStore}
                    currentStore={currentStore}
                    onGoToBasketStore={() => {
                      if (basket?.basketStore?.storeSlug) {
                        navigate(`/${basket.basketStore.storeSlug}`);
                        onClose();
                      }
                    }}
                    onClear={() => dispatch(refreshBasket())}
                  />
                  {basketInMenuOrder(basket.items, menuIndex).map((item) => (
                    <BasketProduct
                      key={item.basketId + '-SidebarNew'}
                      product={item}
                    />
                  ))}

                  {/* Note preview */}
                  {basket.note && (
                    <div
                      className="mt-3"
                      style={{
                        padding: '12px 14px',
                        background: 'var(--ui-surface-2)',
                        borderRadius: '12px',
                        border: '1px solid var(--ui-line)',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '10.5px',
                          fontWeight: 700,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          color: 'var(--ui-ink-3)',
                          marginBottom: '4px',
                        }}
                      >
                        Order note
                      </div>
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'var(--ui-ink)',
                          fontStyle: 'italic',
                          lineHeight: 1.4,
                        }}
                      >
                        {basket.note}
                      </div>
                    </div>
                  )}

                  {/* Quick actions */}
                  <div className="flex gap-2 mt-4">
                    <QuickActionButton
                      icon={<HiOutlineTag size={14} />}
                      label={
                        basket.discountcode ? 'Coupon applied' : 'Add coupon'
                      }
                      onClick={() => setShowCouponModal(true)}
                      active={!!basket.discountcode}
                    />
                    <QuickActionButton
                      icon={<HiOutlinePencilAlt size={14} />}
                      label={basket.note ? 'Edit note' : 'Add note'}
                      onClick={() => setShowNoteModal(true)}
                      active={!!basket.note}
                    />
                  </div>

                  {/* Back to menu link */}
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center gap-[6px] mt-5 mb-1 transition-colors"
                    style={{
                      fontSize: '13px',
                      fontWeight: 600,
                      color: 'var(--ui-ink-2)',
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    <HiOutlineArrowLeft size={14} />
                    Continue browsing
                  </button>
                </div>
              </div>

              {/* ============ COUPON MIN WARNING ============ */}
              {couponMinPriceInfo && (
                <div
                  className="flex-shrink-0"
                  style={{
                    padding: '10px 18px',
                    background: 'rgba(220, 38, 38, 0.06)',
                    borderTop: '1px solid var(--ui-line)',
                    fontSize: '12.5px',
                    color: '#B91C1C',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                >
                  Add ${couponMinPriceInfo.needed.toFixed(2)} more for{' '}
                  {couponMinPriceInfo.discountText}
                </div>
              )}

              {/* ============ PRICE + CHECKOUT ============ */}
              <div
                className="flex-shrink-0 relative"
                style={{
                  padding:
                    '16px 18px max(var(--sab), 16px)',
                  background: 'var(--ui-surface)',
                  borderTop: '1px solid var(--ui-line)',
                }}
              >
                {/* Savings highlight */}
                {hasSavings && (
                  <div
                    className="flex items-center justify-between mb-3"
                    style={{
                      padding: '8px 12px',
                      borderRadius: '10px',
                      background: 'rgba(21, 128, 61, 0.08)',
                      color: 'var(--ui-green)',
                      fontSize: '12.5px',
                      fontWeight: 700,
                    }}
                  >
                    <span className="inline-flex items-center gap-2">
                      <span style={{ fontSize: '14px' }}>✨</span>
                      You're saving
                    </span>
                    <span style={{ fontFamily: 'Geist Mono, monospace' }}>
                      ${getNumberFormat(discount)}
                    </span>
                  </div>
                )}

                {/* Subtotal */}
                <div className="flex items-baseline justify-between mb-3">
                  <span
                    style={{
                      fontSize: '11.5px',
                      fontWeight: 700,
                      color: 'var(--ui-ink-2)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Subtotal
                  </span>
                  <span
                    style={{
                      fontFamily: 'Geist Mono, monospace',
                      fontSize: '22px',
                      fontWeight: 800,
                      color: 'var(--ui-ink)',
                      letterSpacing: '-0.015em',
                      lineHeight: 1,
                    }}
                  >
                    ${getNumberFormat(hasSavings ? subtotal - discount : subtotal)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleCheckout}
                  className="w-full font-bold transition-all inline-flex items-center justify-center gap-2"
                  style={{
                    height: '48px',
                    borderRadius: '12px',
                    fontSize: '14.5px',
                    background: 'var(--action-green)',
                    color: 'var(--action-green-fg)',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow:
                      '0 10px 24px -8px var(--action-green-soft), 0 2px 6px rgba(15, 23, 42, 0.08)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.background =
                      'var(--action-green-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.background = 'var(--action-green)';
                  }}
                >
                  Go to checkout
                  <HiOutlineArrowRight size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Note modal */}
      <Dialog
        contentClassName="max-w-[750px] mx-auto px-10"
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
      >
        <NoteModal
          isOpen={showNoteModal}
          onClose={() => setShowNoteModal(false)}
        />
      </Dialog>

      {/* Coupon modal */}
      <Dialog
        contentClassName="max-w-[450px] mx-auto px-10"
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
      >
        <CouponModal
          isOpen={showCouponModal}
          onClose={() => setShowCouponModal(false)}
        />
      </Dialog>
    </>
  );
};

// ============= Sub-components =============

const QuickActionButton = ({ icon, label, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex-1 inline-flex items-center justify-center gap-2 transition-colors"
    style={{
      padding: '10px 12px',
      fontSize: '12.5px',
      fontWeight: 600,
      borderRadius: '10px',
      background: active
        ? 'var(--accent-soft, var(--site-color-10))'
        : 'var(--ui-surface-2)',
      color: active
        ? 'var(--accent, var(--site-color))'
        : 'var(--ui-ink-2)',
      border: `1px solid ${active ? 'var(--accent-border, var(--site-color-10))' : 'var(--ui-line)'}`,
      cursor: 'pointer',
      fontFamily: 'inherit',
    }}
  >
    {icon}
    {label}
  </button>
);

const EmptyState = ({ onClose }) => (
  <div className="flex-1 flex items-center justify-center p-8">
    <div className="text-center max-w-sm">
      <div
        className="w-20 h-20 mx-auto mb-5 grid place-items-center"
        style={{
          borderRadius: '50%',
          background: 'var(--ui-surface-2)',
          border: '1px solid var(--ui-line)',
        }}
      >
        <HiOutlineShoppingBag
          size={32}
          style={{ color: 'var(--ui-ink-3)' }}
        />
      </div>
      <h3
        style={{
          fontSize: '18px',
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: 'var(--ui-ink)',
          marginBottom: '6px',
        }}
      >
        Your cart is empty
      </h3>
      <p
        className="mb-6"
        style={{
          fontSize: '13.5px',
          color: 'var(--ui-ink-2)',
          lineHeight: 1.5,
        }}
      >
        Browse the menu and add a few delicious things to get started.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="w-full inline-flex items-center justify-center gap-2 transition-all"
        style={{
          padding: '12px 18px',
          borderRadius: '12px',
          background: 'var(--ui-ink)',
          color: '#FFFFFF',
          fontWeight: 700,
          fontSize: '14px',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        Continue browsing
        <HiOutlineArrowRight size={14} />
      </button>
    </div>
  </div>
);

const CrossStoreBanner = ({
  basketStore,
  currentStore,
  onGoToBasketStore,
  onClear,
}) => {
  const basketSlug = basketStore?.storeSlug;
  const currentSlug = currentStore?.slug;
  if (!basketSlug || !currentSlug || basketSlug === currentSlug) return null;
  return (
    <div
      className="mb-3"
      style={{
        padding: '12px 14px',
        borderRadius: '12px',
        background: 'rgba(217, 119, 6, 0.08)',
        border: '1px solid rgba(217, 119, 6, 0.28)',
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
      }}
    >
      <HiOutlineExclamation
        size={18}
        style={{ color: '#B45309', flexShrink: 0, marginTop: '1px' }}
      />
      <div className="flex-1 min-w-0">
        <div
          style={{
            fontSize: '12.5px',
            fontWeight: 700,
            color: 'var(--ui-ink)',
            letterSpacing: '-0.01em',
            marginBottom: '2px',
          }}
        >
          These items are from another restaurant
        </div>
        <div
          style={{
            fontSize: '12px',
            color: 'var(--ui-ink-2)',
            lineHeight: 1.5,
            marginBottom: '8px',
          }}
        >
          Your cart has items from{' '}
          <strong style={{ color: 'var(--ui-ink)', fontWeight: 700 }}>
            {basketStore?.name || 'another restaurant'}
          </strong>
          . You can keep browsing {currentStore?.name || 'this one'}, but to
          check out you&apos;ll need to finish that order or empty the cart.
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onGoToBasketStore}
            style={{
              padding: '6px 11px',
              borderRadius: '8px',
              background: 'var(--ui-ink)',
              color: '#FFFFFF',
              border: 'none',
              fontSize: '11.5px',
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Back to {basketStore?.name || 'that store'}
          </button>
          <button
            type="button"
            onClick={onClear}
            style={{
              padding: '6px 11px',
              borderRadius: '8px',
              background: 'transparent',
              color: 'var(--ui-ink-2)',
              border: '1px solid var(--ui-line-2)',
              fontSize: '11.5px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Empty cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartSidebar;
